// API client tipado pra `/api/crm/activities` (Story 11.8 — timeline + minhas tarefas).
// Padrão: wrapper sobre `apiClient` (axios) + Zod parse nas respostas (Tier 2).
//
// Fonte de tipos: schemas Zod do `@control-center/shared` (back+front).
// Convenção de envelope do backend:
//  - lista: { items, meta: { next_cursor, total } }
//  - single: { data }

import { z } from 'zod';

import {
  crmActivityTypeSchema,
  type CreateCrmActivityDto,
  type UpdateCrmActivityDto,
} from '@control-center/shared';

import { apiClient } from './api-client';

// -----------------------------------------------------------------------------
// Schemas de resposta (Tier 2 — valida o shape que o backend devolve)
// -----------------------------------------------------------------------------

const targetSchema = z
  .object({
    kind: z.enum(['lead', 'deal', 'client']),
    id: z.string(),
    name: z.string(),
  })
  .nullable();

const authorSchema = z.object({ id: z.string(), name: z.string() }).nullable();

const crmActivitySchema = z.object({
  id: z.string().uuid(),
  type: crmActivityTypeSchema,
  subject: z.string(),
  description: z.string().nullable(),
  author_user_id: z.string().nullable(),
  lead_id: z.string().nullable(),
  deal_id: z.string().nullable(),
  client_id: z.string().nullable(),
  due_date: z.string().nullable(),
  done: z.boolean(),
  done_at: z.string().nullable(),
  is_system: z.boolean(),
  occurred_at: z.string(),
  created_at: z.string(),
  target: targetSchema,
  author: authorSchema,
});
export type CrmActivityEntity = z.infer<typeof crmActivitySchema>;

const listResponseSchema = z.object({
  items: z.array(crmActivitySchema),
  meta: z.object({ next_cursor: z.string().nullable(), total: z.number() }),
});
export type CrmActivityListResponse = z.infer<typeof listResponseSchema>;

const singleResponseSchema = z.object({ data: crmActivitySchema });

/** Alvo polimórfico — exatamente 1 fica preenchido (consumido pela timeline 11.3). */
export type CrmActivityTarget = { lead_id: string } | { deal_id: string } | { client_id: string };

export interface CrmActivityListParams {
  cursor?: string;
  limit?: number;
  lead_id?: string;
  deal_id?: string;
  client_id?: string;
  type?: CrmActivityEntity['type'];
  author_user_id?: string;
  done?: boolean;
  due_before?: string;
  my_tasks?: boolean;
}

/** GET /api/crm/activities — lista paginada por cursor (timeline OU minhas tarefas). */
export async function fetchCrmActivities(
  params: CrmActivityListParams = {},
  signal?: AbortSignal,
): Promise<CrmActivityListResponse> {
  const { data } = await apiClient.get('/api/crm/activities', { params, signal });
  return listResponseSchema.parse(data);
}

/** POST /api/crm/activities — cria atividade/tarefa. */
export async function createCrmActivity(payload: CreateCrmActivityDto): Promise<CrmActivityEntity> {
  const { data } = await apiClient.post('/api/crm/activities', payload);
  return singleResponseSchema.parse(data).data;
}

/** PUT /api/crm/activities/:id — edita / conclui tarefa (done). */
export async function updateCrmActivity(
  id: string,
  payload: UpdateCrmActivityDto,
): Promise<CrmActivityEntity> {
  const { data } = await apiClient.put(`/api/crm/activities/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}
